import { useEffect, useState } from 'react';

export default function HeroSection() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(t);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden">
      {/* Background — DJ performing at a packed event */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/2d16b351a883eda9775d6808df3b707c.jpg"
          alt="Bugsie Productions DJ performing at a live event"
          className="w-full h-full object-cover object-center"
          style={{
            transform: visible ? 'scale(1.03)' : 'scale(1.08)',
            transition: 'transform 8s ease-out',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/75 to-black/30"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-black/40"></div>
        <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#0A0A0A] to-transparent"></div>
      </div>

      {/* Decorative vertical line */}
      <div className="absolute left-10 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-3">
        <div className="w-px h-24 bg-gradient-to-b from-transparent to-[#D4AF37]/60"></div>
        <div className="w-1.5 h-1.5 rounded-full bg-[#D4AF37]"></div>
        <div className="w-px h-24 bg-gradient-to-b from-[#D4AF37]/60 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 pt-28 pb-24">
        <div
          className="max-w-[700px]"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(28px)',
            transition: 'opacity 0.9s ease-out, transform 0.9s ease-out',
          }}
        >
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-px bg-[#D4AF37]"></div>
            <span className="text-[#D4AF37] text-xs tracking-[0.35em] uppercase font-semibold">
              Seattle &amp; Puget Sound's Premier DJ
            </span>
          </div>

          {/* H1 */}
          <h1
            className="font-black text-white leading-[1.04] mb-7"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(2.6rem, 5.5vw, 5rem)',
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(20px)',
              transition: 'opacity 1s ease-out 0.15s, transform 1s ease-out 0.15s',
            }}
          >
            The DJ &amp; Entertainment<br />
            That Makes Your<br />
            <em className="text-[#D4AF37] not-italic">Event Unforgettable</em>
          </h1>

          {/* Subheadline */}
          <p
            className="text-gray-300 text-lg md:text-xl leading-relaxed mb-10 max-w-[540px]"
            style={{
              opacity: visible ? 1 : 0,
              transition: 'opacity 1s ease-out 0.3s',
            }}
          >
            Professional DJ, emcee, photo booth, karaoke, and game show services for weddings, corporate events, school dances, and private parties across Seattle &amp; the Puget Sound.
          </p>

          {/* CTAs */}
          <div
            className="flex flex-col sm:flex-row gap-4"
            style={{
              opacity: visible ? 1 : 0,
              transition: 'opacity 1s ease-out 0.45s',
            }}
          >
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollTo('#contact'); }}
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] hover:bg-[#c49d2a] text-black font-bold px-10 py-4 rounded-full text-sm tracking-widest uppercase transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              Get a Quote
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm group-hover:translate-x-1 transition-transform duration-300"></i>
              </div>
            </a>
            <a
              href="tel:2069319361"
              className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-[#D4AF37] text-white hover:text-[#D4AF37] font-semibold px-10 py-4 rounded-full text-sm tracking-widest uppercase transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-sm"></i>
              </div>
              Call Now
            </a>
          </div>

          {/* Social proof strip */}
          <div
            className="flex flex-wrap items-center gap-5 mt-12 pt-8 border-t border-white/10"
            style={{
              opacity: visible ? 1 : 0,
              transition: 'opacity 1s ease-out 0.6s',
            }}
          >
            <div className="flex items-center gap-1">
              {[1,2,3,4,5].map(i => (
                <div key={i} className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-star-fill text-[#D4AF37] text-sm"></i>
                </div>
              ))}
            </div>
            <span className="text-gray-400 text-sm">
              <strong className="text-white">5.0</strong> · 140+ reviews · DJ &amp; entertainment since 2001
            </span>
            <span className="hidden sm:block text-gray-700">|</span>
            <span className="text-gray-500 text-sm">Weddings · Corporate · Schools · Parties</span>
          </div>
        </div>
      </div>

      {/* Scroll cue */}
      <div
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 cursor-pointer"
        onClick={() => scrollTo('#trust')}
        style={{
          opacity: visible ? 1 : 0,
          transition: 'opacity 1.2s ease-out 1s',
        }}
      >
        <span className="text-gray-600 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#D4AF37]/60 to-transparent animate-pulse"></div>
      </div>
    </section>
  );
}
